import { z } from 'zod';
import { refersTo } from '../common/refers-to';

/**
 * Admin member impersonation (IMM-346). Two surfaces:
 *
 *  - `start` (admin tier, `admin.immoos.ai`) creates a time-bound
 *    impersonation session against a workspace member and returns a
 *    single-use handoff code. The code carries no authority — it is a
 *    short-lived pointer at the already-created session.
 *  - `exchange` (customer tier, `app.immoos.ai`) burns the handoff code and
 *    returns the opaque operating credential the customer app then presents
 *    as a bearer. The credential never appears in a URL.
 *
 * The read-only / read-write `mode` field and its audit-suppression
 * behaviour are a deliberate follow-up; the foundation operates at the
 * impersonated member's own authority, bound to that member's single workspace
 * (IMM-23), minus the hard-block list.
 */

export const StartImpersonationBodySchema = z.object({
  // The admin picks the member to act as; the session is bound to that member
  // and its workspace (IMM-23) — authority is that member's own, only in that
  // workspace. The member's user and workspace are derived server-side.
  memberId: z.uuid(),
  reason: z.string().trim().max(500).optional(),
  /**
   * Fresh TOTP step-up code. Optional at the contract level only because the
   * server-side step-up primitive is not yet built (IMM-346 leaves it as a
   * marked seam); once TOTP infrastructure lands it becomes required and the
   * step-up verifier rejects a missing/stale code.
   */
  stepUpCode: z.string().trim().optional(),
});

export type StartImpersonationBody = z.infer<typeof StartImpersonationBodySchema>;

export const StartImpersonationResponseSchema = z.object({
  sessionId: z.uuid(),
  // Single-use, short-lived (≈30–60s). Exchanged exactly once for the
  // operating credential, then burned. Carries no authority on its own.
  handoffCode: z.string(),
  expiresAt: z.iso.datetime(),
});

export type StartImpersonationResponse = z.infer<typeof StartImpersonationResponseSchema>;

export const ExchangeImpersonationBodySchema = z.object({
  code: z.string().trim().min(1),
});

export type ExchangeImpersonationBody = z.infer<typeof ExchangeImpersonationBodySchema>;

export const ExchangeImpersonationResponseSchema = z.object({
  // Opaque operating credential, presented as a bearer by the customer app.
  // Backed by the session row; validated per request, so revocation/expiry
  // take effect within one request cycle.
  token: z.string(),
  workspaceId: z.uuid(),
  impersonatedMemberId: refersTo('member', z.uuid()),
  expiresAt: z.iso.datetime(),
});

export type ExchangeImpersonationResponse = z.infer<typeof ExchangeImpersonationResponseSchema>;
